import React, { useState } from 'react';
import { useSubscription } from '../hooks/useSubscription';
import { CreditCard, ShieldCheck, CheckCircle2, Clock, Zap, ExternalLink, Loader2, Sparkles } from 'lucide-react';
import { stripeService } from '../services/stripeService';
import { updateTenant, PlanId } from '../services/tenantService';

interface Props {
  tenantId: string;
}

export const BillingSettings: React.FC<Props> = ({ tenantId }) => {
  const sub = useSubscription(tenantId);
  const [isProcessing, setIsProcessing] = useState(false);
  const [msg, setMsg] = useState('');

  const handleInitializeStripe = async (planId: string) => {
    console.log('[BillingSettings] handleInitializeStripe called, planId:', planId);
    setIsProcessing(true);
    setMsg('');
    try {
      console.log('[BillingSettings] Creating checkout session...');
      const { url } = await stripeService.createCheckoutSession(tenantId, planId);
      console.log('[BillingSettings] Session created, opening URL:', url);
      window.open(url, '_blank', 'noreferrer,noopener');
    } catch (error: any) {
      console.error('[BillingSettings] Stripe error:', error);
      const detail = error.response?.data?.details || error.message || 'Transaction failed. Please try again.';
      setMsg(detail);
    } finally {
      console.log('[BillingSettings] Finally block, setting isProcessing to false');
      setIsProcessing(false);
    }
  };

  const syncUsage = async () => {
    try {
      const { count } = await stripeService.reportUserUsage(tenantId);
      setMsg(`Usage synced successfully! Reported ${count} active users.`);
    } catch (error) {
       setMsg('Failed to sync usage. Subscription may not be active yet.');
    }
  };

  const handleManualPlanSwitch = async (planId: PlanId) => {
    try {
      await updateTenant(tenantId, { planId });
      setMsg(`Successfully switched to ${planId} plan!`);
      window.location.reload(); // Refresh to update hook state
    } catch (error) {
      setMsg('Failed to switch plan.');
    }
  };

  return (
    <div className="space-y-10 max-w-7xl mx-auto font-sans">
      <div className="border-b border-gray-50 pb-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h2 className="text-xl md:text-3xl font-black text-gray-900 tracking-tight uppercase">Billing & Subscription</h2>
          <p className="text-gray-500 mt-1 font-medium text-xs md:text-base">Manage your service tier and payment instrumentation.</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-2xl border border-gray-100">
           <span className="text-[10px] font-black uppercase text-gray-400">Current Plan:</span>
           <span className="text-sm font-black text-blue-600 uppercase italic underline decoration-blue-600/30 underline-offset-4">{sub.planId}</span>
        </div>
      </div>

      {msg && (
        <div className={`p-4 rounded-2xl font-bold text-center animate-in fade-in slide-in-from-top-2 ${msg.includes('Successfully') ? 'bg-emerald-50 text-emerald-700 border border-emerald-100' : 'bg-rose-50 text-rose-700 border border-rose-100'}`}>
          {msg}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Base Plan */}
        <div className={`card overflow-hidden border-2 transition-all flex flex-col ${sub.planId === 'base' ? 'border-blue-600 shadow-2xl shadow-blue-500/10 scale-[1.02] z-10' : 'border-gray-100'}`}>
          <div className="bg-gray-900 p-6 text-white text-center">
            <h3 className="text-xl font-black italic tracking-tighter uppercase mb-1">Base Plan</h3>
            <p className="opacity-80 text-[10px] font-bold uppercase tracking-widest">Core Tip Tracking</p>
          </div>
          <div className="p-6 bg-white flex-1 flex flex-col space-y-6">
            <div className="text-center">
              <span className="text-4xl font-black italic tracking-tighter">$19.99</span>
              <span className="text-gray-400 font-bold text-xs tracking-widest uppercase">/ mo</span>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-2">+ $6.00 / user fee</p>
            </div>
            <ul className="space-y-2 flex-1">
              {[
                '1 Store Location',
                'Tip Jar & Personal Cash Reporting',
                'POS CSV Shift & Hours Import',
                'Tip Allocations (Hours & Points)',
                'Automated Flux & Reconciliations',
                'Employee Approvals & Disputes',
                'IRS Form 4070 Cash Reporting',
                'Standard Tip Payroll CSV Export',
                'IRS 8% Gross Receipts Shortfall',
                'In-App Team Chat & Announcements'
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[10px] font-bold text-gray-600">
                  <CheckCircle2 size={12} className="text-emerald-500 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button 
              onClick={() => handleInitializeStripe('base')}
              disabled={sub.planId === 'base' || isProcessing}
              className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${
                sub.planId === 'base' ? 'bg-gray-100 text-gray-400' : 'bg-gray-900 text-white hover:bg-black shadow-lg shadow-gray-900/10'
              }`}
            >
              {sub.planId === 'base' ? 'Current' : 'Select Base'}
            </button>
          </div>
        </div>

        {/* Enhanced Plan */}
        <div className={`card overflow-hidden border-2 transition-all flex flex-col ${sub.planId === 'enhanced' ? 'border-blue-600 shadow-2xl shadow-blue-500/10 scale-[1.02] z-10' : 'border-gray-100'}`}>
          <div className="bg-blue-600 p-6 text-white text-center">
            <h3 className="text-xl font-black italic tracking-tighter uppercase mb-1">Enhanced</h3>
            <p className="opacity-80 text-[10px] font-bold uppercase tracking-widest">Analytics & Scale</p>
          </div>
          <div className="p-6 bg-white flex-1 flex flex-col space-y-6">
            <div className="text-center">
              <span className="text-4xl font-black italic tracking-tighter">$39.99</span>
              <span className="text-gray-400 font-bold text-xs tracking-widest uppercase">/ mo</span>
              <p className="text-[10px] text-blue-600 font-black uppercase tracking-widest mt-2">+ $5.00 / user fee</p>
            </div>
            <ul className="space-y-2 flex-1">
              <li className="text-[10px] font-black text-blue-600 uppercase tracking-widest border-b pb-1 mb-2">Base + inclusions:</li>
              {[
                'Up to 3 Store Locations',
                'Benchmark Dashboard & Trends',
                'Team Earning Velocity',
                'Shift Performance Analytics',
                'Multi-Store Batch Payroll Extracts'
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[10px] font-bold text-gray-600">
                  <Zap size={12} className="text-blue-500 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button 
              onClick={() => handleInitializeStripe('enhanced')}
              disabled={sub.planId === 'enhanced' || isProcessing}
              className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${
                sub.planId === 'enhanced' ? 'bg-blue-100 text-blue-600' : 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg shadow-blue-500/20'
              }`}
            >
              {sub.planId === 'enhanced' ? 'Current' : 'Upgrade'}
            </button>
          </div>
        </div>

        {/* Enterprise Plan */}
        <div className={`card overflow-hidden border-2 transition-all flex flex-col ${sub.planId === 'enterprise' ? 'border-purple-600 shadow-2xl shadow-purple-500/10 scale-[1.02] z-10' : 'border-gray-100'}`}>
          <div className="bg-purple-900 p-6 text-white text-center">
            <h3 className="text-xl font-black italic tracking-tighter uppercase mb-1">Enterprise</h3>
            <p className="opacity-80 text-[10px] font-bold uppercase tracking-widest">Full Suite + Support</p>
          </div>
          <div className="p-6 bg-white flex-1 flex flex-col space-y-6">
            <div className="text-center">
              <span className="text-4xl font-black italic tracking-tighter">$79.99</span>
              <span className="text-gray-400 font-bold text-xs tracking-widest uppercase">/ mo</span>
              <p className="text-[10px] text-purple-600 font-black uppercase tracking-widest mt-2">+ $3.50 / user fee</p>
            </div>
            <ul className="space-y-2 flex-1">
              <li className="text-[10px] font-black text-purple-600 uppercase tracking-widest border-b pb-1 mb-2">Enhanced + inclusions:</li>
              {[
                'Up to 5 Store Locations',
                'Cross-Store Executive Rollups',
                'Raw, Unformatted Data Extracts',
                'Custom Payroll Formats (Coming Soon)',
                'REST API & Webhooks (Coming Soon)',
                'Priority Dedicated Support & Onboarding'
              ].map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[10px] font-bold text-gray-600">
                  <ShieldCheck size={12} className="text-purple-500 shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>
            <button 
              onClick={() => handleInitializeStripe('enterprise')}
              disabled={sub.planId === 'enterprise' || isProcessing}
              className={`w-full py-3 rounded-xl font-black uppercase tracking-widest text-[10px] transition-all ${
                sub.planId === 'enterprise' ? 'bg-purple-100 text-purple-600' : 'bg-purple-900 text-white hover:bg-black shadow-lg shadow-purple-900/10'
              }`}
            >
              {sub.planId === 'enterprise' ? 'Current' : 'Go Enterprise'}
            </button>
          </div>
        </div>

      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="card p-6 border border-gray-100 bg-gray-50/50">
          <div className="flex items-start gap-4 text-left">
            <div className="p-3 bg-white rounded-2xl shadow-sm">
              <Clock size={24} className="text-blue-600" />
            </div>
            <div className="flex-1">
              <h4 className="font-black text-gray-900 uppercase tracking-tight italic">Trial Status</h4>
              <p className="text-xs text-gray-500 font-medium mb-4 italic">Setup takes <span className="text-blue-600 font-black">10 minutes</span></p>
              <div className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest leading-none mb-1">Time Remaining</p>
                  <p className="text-lg font-black text-gray-900">{sub.trialDaysLeft} Days</p>
                </div>
                <div className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${sub.isExpired ? 'bg-rose-50 text-rose-500' : 'bg-emerald-50 text-emerald-600'}`}>
                  {sub.isExpired ? 'Expired' : 'In Trial'}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card p-6 border border-gray-100 space-y-4">
          <button 
            onClick={sub.refetch}
            className="w-full py-3 bg-gray-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-blue-600 hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
          >
             <Sparkles size={14} /> Force Refresh Data
          </button>
          <button 
            onClick={() => handleInitializeStripe('')}
            disabled={isProcessing}
            className="w-full py-3 bg-gray-50 border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-[0.2em] text-gray-900 hover:bg-gray-100 transition-all flex items-center justify-center gap-2"
          >
            {isProcessing ? <Loader2 className="animate-spin" size={14} /> : <ExternalLink size={14} />}
            Manage via Stripe Billing
          </button>
        </div>
      </div>
    </div>
  );
};
